"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocale, useTranslations } from "next-intl";
import { usePathname } from "@/i18n/navigation";
import {
  LOCALES,
  LOCALE_COOKIE,
  LOCALE_COOKIE_MAX_AGE_SECONDS,
  localeDefinition,
  toLocale,
} from "@/i18n/config";
import { localizePath } from "@/i18n/detect";
import { TWITCH_CHANNEL_URL } from "@/data/schedule";
import { STEAM_WISHLIST_URL } from "@/data/scoring";

interface ComingSoonMobileNavProps {
  links: ReadonlyArray<{
    href: string;
    label: string;
  }>;
  variant?: "coming-soon" | "live" | "post";
}

export function ComingSoonMobileNav({
  links,
  variant = "coming-soon",
}: ComingSoonMobileNavProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const locale = toLocale(useLocale());
  const pathname = usePathname();
  const t = useTranslations("components.languageSwitcher");
  const tNav = useTranslations("nav");
  const tPost = useTranslations("pages.home.post.legacy");
  const rootClassName =
    variant === "post"
      ? "post-mobile-nav"
      : variant === "live"
        ? "live-mobile-nav"
        : "cs-mobile-nav";
  const toggleClassName =
    variant === "live" ? "live-mobile-nav-toggle" : "cs-mobile-nav-toggle";
  const menuClassName =
    variant === "post"
      ? "post-mobile-nav-menu"
      : variant === "live"
        ? "live-mobile-nav-menu"
        : "cs-mobile-nav-menu";
  const linksClassName =
    variant === "live" ? "live-mobile-nav-links" : "cs-mobile-nav-links";
  const itemClassName =
    variant === "live" ? "live-mobile-nav-item" : "cs-mobile-nav-item";
  const ruleClassName =
    variant === "live" ? "live-mobile-nav-rule" : "cs-mobile-nav-rule";
  const menuId =
    variant === "post"
      ? "post-mobile-nav-menu"
      : variant === "live"
        ? "live-mobile-nav-menu"
        : "cs-mobile-nav-menu";

  useEffect(() => {
    if (!isOpen || variant !== "post") return;
    const previous = document.body.style.overflow;
    const previousFocus = document.activeElement as HTMLElement | null;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
      previousFocus?.focus();
    };
  }, [isOpen, variant]);

  useEffect(() => {
    setIsMounted(true);

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setIsOpen(false);
    }

    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, []);

  function chooseLanguage(code: string) {
    if (code === locale) return;
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${LOCALE_COOKIE}=${code}; Path=/; Max-Age=${LOCALE_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
    const href = localizePath(pathname, code);
    window.location.assign(
      `${href}${window.location.search}${window.location.hash}`,
    );
  }

  return (
    <div className={isOpen ? `${rootClassName} is-open` : rootClassName}>
      <button
        type="button"
        className={toggleClassName}
        aria-label={isOpen ? "Close navigation" : "Open navigation"}
        aria-expanded={isOpen}
        aria-controls={menuId}
        onClick={() => setIsOpen((open) => !open)}
      >
        <img
          src={isOpen ? "/cs/nav-mobile-x.svg" : "/cs/nav-mobile-default.svg"}
          alt=""
          aria-hidden="true"
        />
      </button>
      {isMounted &&
        (variant !== "post" || isOpen) &&
        createPortal(
          <div
            id={menuId}
            className={isOpen ? `${menuClassName} is-open` : menuClassName}
            role="dialog"
            aria-modal="true"
            aria-hidden={!isOpen}
            aria-label={tNav("label")}
            onKeyDown={(event) => {
              if (variant !== "post" || event.key !== "Tab") return;
              const controls =
                event.currentTarget.querySelectorAll<HTMLElement>(
                  "button, a[href]",
                );
              const first = controls[0];
              const last = controls[controls.length - 1];
              if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last?.focus();
              } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first?.focus();
              }
            }}
          >
            {variant === "post" && (
              <>
                <button
                  type="button"
                  className="post-mobile-close"
                  aria-label="Close navigation"
                  onClick={() => setIsOpen(false)}
                  autoFocus
                >
                  <img src="/cs/nav-mobile-x.svg" alt="" aria-hidden="true" />
                </button>
                <div className="post-mobile-intro">
                  <p>{tPost("kicker")}</p>
                  <a
                    className="post-nav-steam"
                    href={STEAM_WISHLIST_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {tPost("playNow")}
                  </a>
                </div>
              </>
            )}
            <div className={linksClassName}>
              <div className="cs-mobile-nav-items">
                {links.map((link, index) => (
                  <div className={itemClassName} key={link.href}>
                    {index > 0 && (
                      <img
                        src="/cs/nav-line.svg"
                        className={ruleClassName}
                        alt=""
                        aria-hidden="true"
                      />
                    )}
                    <a href={link.href} onClick={() => setIsOpen(false)}>
                      {link.label}
                    </a>
                  </div>
                ))}
              </div>
              <div
                className="cs-mobile-language-picker"
                aria-label="Language selection"
              >
                <span className="cs-mobile-language-title">{t("trigger")}</span>
                <div className="cs-mobile-language-options">
                  {LOCALES.map((code) => (
                    <button
                      key={code}
                      type="button"
                      className={code === locale ? "is-current" : undefined}
                      aria-current={code === locale ? "true" : undefined}
                      lang={code}
                      onClick={() => chooseLanguage(code)}
                    >
                      {localeDefinition(code).label}
                    </button>
                  ))}
                </div>
                {variant === "live" && (
                  <a
                    className="mobile-nav-watch-live"
                    href={TWITCH_CHANNEL_URL}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={tNav("watchLive")}
                  >
                    <img
                      src="/cs/twitch.svg"
                      alt=""
                      aria-hidden="true"
                      width={12}
                      height={12}
                    />
                    <span>{tNav("watchLive")}</span>
                  </a>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
